import type { ApiError, ServiceSlug } from "./types";
import type { TypeGuard } from "../types/common";
import { isApiError } from "./guards";

const API_BASE_URL = location.origin;

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

// Global access token getter (set by AuthContext on mount)
let getAccessToken: (() => string | null) | null = null;
// Optional refresh callback; when set, 401 responses trigger refresh and one retry
let refreshAccessToken: (() => Promise<string | null>) | null = null;

export function setAccessTokenGetter(fn: () => string | null): void {
  getAccessToken = fn;
}

export function setRefreshCallback(fn: () => Promise<string | null>): void {
  refreshAccessToken = fn;
}

export interface FetchJsonOptions {
  method?: HttpMethod;
  searchParams?: Record<
    string,
    string | string[] | number | boolean | undefined | null
  >;
  body?: unknown;
  serviceSlug?: ServiceSlug;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  skipAuth?: boolean; // Set to true for public endpoints (auth/login, etc.)
}

export async function fetchJson<TResponse>(
  path: string,
  options: FetchJsonOptions,
  validate?: TypeGuard<TResponse>,
): Promise<TResponse> {
  const url = new URL("/api/query" + path, API_BASE_URL);

  if (options.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) {
          url.searchParams.append(key, String(v));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers ?? {}),
  };
  if (options.serviceSlug) {
    headers["X-OpenMon-Service"] = options.serviceSlug;
  }
  // Automatically add JWT token unless skipAuth is true or token is already in headers
  if (!options.skipAuth && !headers.Authorization && getAccessToken) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  if (options.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  let res = await fetch(url.toString(), {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : typeof options.body === "string"
          ? options.body
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  // On 401 with auth enabled, try refresh once and retry
  if (
    res.status === 401 &&
    !options.skipAuth &&
    refreshAccessToken &&
    getAccessToken
  ) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
      res = await fetch(url.toString(), {
        method: options.method ?? "GET",
        headers: retryHeaders,
        body:
          options.body === undefined
            ? undefined
            : typeof options.body === "string"
              ? options.body
              : JSON.stringify(options.body),
        signal: options.signal,
      });
    }
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      // If the server responds with non-JSON text, keep it as-is for better error messages.
      data = text;
    }
  }

  if (!res.ok) {
    const message =
      (isApiError(data) ? (data as ApiError).error : undefined) ||
      (typeof data === "string" && data.trim() ? data : undefined) ||
      res.statusText ||
      "Request failed";
    throw new Error(message);
  }

  if (validate && !validate(data)) {
    throw new Error("Invalid response from server");
  }

  return data as TResponse;
}

/** Like fetchJson but returns both parsed data and the Response (e.g. to read X-OpenMon-Aggregated). */
export async function fetchJsonWithResponse<TResponse>(
  path: string,
  options: FetchJsonOptions,
  validate?: TypeGuard<TResponse>,
): Promise<{ data: TResponse; response: Response }> {
  const url = new URL("/api/query" + path, API_BASE_URL);

  if (options.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) {
          url.searchParams.append(key, String(v));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers ?? {}),
  };
  if (options.serviceSlug) {
    headers["X-OpenMon-Service"] = options.serviceSlug;
  }
  if (!options.skipAuth && !headers.Authorization && getAccessToken) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (options.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  let res = await fetch(url.toString(), {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : typeof options.body === "string"
          ? options.body
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (
    res.status === 401 &&
    !options.skipAuth &&
    refreshAccessToken &&
    getAccessToken
  ) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
      res = await fetch(url.toString(), {
        method: options.method ?? "GET",
        headers: retryHeaders,
        body:
          options.body === undefined
            ? undefined
            : typeof options.body === "string"
              ? options.body
              : JSON.stringify(options.body),
        signal: options.signal,
      });
    }
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const message =
      (isApiError(data) ? (data as ApiError).error : undefined) ||
      (typeof data === "string" && data.trim() ? data : undefined) ||
      res.statusText ||
      "Request failed";
    throw new Error(message);
  }

  if (validate && !validate(data)) {
    throw new Error("Invalid response from server");
  }

  return { data: data as TResponse, response: res };
}
